import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { loadPeaks } from "../../lib/waveformPeaks";
import { midColor, rgba } from "../../lib/kitColor";

// A catalogue row's waveform, drawn like the plugin's Sample Editor: the
// kit's colours as one vertical min/max line per pixel, straight on the row
// with no panel, and the beat grid behind it. While the row's preview plays,
// the part already heard lights up and a playhead sweeps across, the way
// Splice rows do.
//
// Peaks come from the preview audio itself (see lib/waveformPeaks), fetched
// only once the row scrolls near the viewport, so a long page never decodes
// rows nobody looks at.

const BEAT_LINE = "#222225";
const BAR_LINE = "#303034";
const PAD_X = 0;
const HEIGHT_FRACTION = 0.42; // of the height, per side: the plugin's 0.46 less a hair of headroom

// The coloured waveform on its own transparent layer, so the gloss below
// can whiten only the waveform and never the grid behind it. Built once
// per kit, size and colour, then reused by every animation frame.
const layers = new WeakMap(); // visible canvas -> { key, canvas }

function waveLayer(target, { peaks, color, w, h, dpr, left, width, mid, half }) {
  const key = `${w}x${h}@${dpr}|${color.from}|${color.to}`;
  const cached = layers.get(target);
  if (cached && cached.key === key && cached.peaks === peaks) return cached.canvas;

  const canvas = cached?.canvas || document.createElement("canvas");
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  // One path for the whole waveform (a rect per pixel column), filled once:
  // far cheaper than a fill per column.
  const bins = peaks.max.length;
  const wave = new Path2D();
  for (let px = 0; px < width; px += 1) {
    const b0 = Math.floor((px / width) * bins);
    const b1 = Math.max(b0 + 1, Math.floor(((px + 1) / width) * bins));
    let lo = 0;
    let hi = 0;
    for (let b = b0; b < b1 && b < bins; b += 1) {
      if (peaks.min[b] < lo) lo = peaks.min[b];
      if (peaks.max[b] > hi) hi = peaks.max[b];
    }
    const top = mid - hi * half;
    const bottom = mid - lo * half;
    wave.rect(left + px, top, 1, Math.max(1, bottom - top));
  }

  // The kit's gradient runs across the loop, left to right...
  const fill = ctx.createLinearGradient(left, 0, left + width, 0);
  fill.addColorStop(0, rgba(color.from, 1));
  fill.addColorStop(1, rgba(color.to, 1));
  ctx.fillStyle = fill;
  ctx.fill(wave);

  // ...with a gloss that lightens the peaks, painted only where the
  // waveform already is.
  const gloss = ctx.createLinearGradient(0, mid - half, 0, mid + half);
  gloss.addColorStop(0, "rgba(255, 255, 255, 0.4)");
  gloss.addColorStop(0.5, "rgba(255, 255, 255, 0)");
  gloss.addColorStop(1, "rgba(255, 255, 255, 0.4)");
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = gloss;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";

  layers.set(target, { key, peaks, canvas });
  return canvas;
}

function draw(canvas, { peaks, color, numBars, progress }) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return;
  const tw = Math.round(w * dpr);
  const th = Math.round(h * dpr);
  if (canvas.width !== tw || canvas.height !== th) {
    canvas.width = tw;
    canvas.height = th;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);


  const left = PAD_X;
  const width = Math.max(1, w - PAD_X * 2);
  const beats = Math.max(1, Math.round(numBars || 0) * 4);
  for (let beat = 1; beat < beats; beat += 1) {
    const x = Math.round(left + (beat / beats) * width) + 0.5;
    ctx.fillStyle = beat % 4 === 0 ? BAR_LINE : BEAT_LINE;
    ctx.fillRect(x - 0.5, 0, 1, h);
  }

  const mid = h / 2;
  const half = h * HEIGHT_FRACTION;

  if (!peaks) {
    ctx.fillStyle = "rgba(255, 255, 255, 0.10)";
    ctx.fillRect(left, Math.round(mid), width, 1);
    return;
  }

  const layer = waveLayer(canvas, { peaks, color, w, h, dpr, left, width, mid, half });
  const glowColor = rgba(midColor(color), 0.6);
  const active = progress != null;
  const headX = active ? left + progress * width : -1;

  const region = (x0, x1, alpha, glow) => {
    if (x1 <= x0) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, 0, x1 - x0, h);
    ctx.clip();
    ctx.globalAlpha = alpha;
    if (glow) {
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 12;
    }
    ctx.drawImage(layer, 0, 0, w, h);
    ctx.restore();
  };

  if (active) {
    region(0, headX, 1, true);
    region(headX, w, 0.28, false);
  } else {
    region(0, w, 0.9, false);
  }

  if (active) {
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fillRect(Math.round(headX), 0, 1.5, h);
  }
}

// `active` means this row's preview is playing right now.
// `color` is the row's { from, to } gradient (see assignRowColors).
export default function KitWaveform({ color, url, numBars, active, getPosition, getDuration }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [peaks, setPeaks] = useState(null);

  // Start decoding once the row is within a screen of the viewport.
  useEffect(() => {
    const el = wrapRef.current;
    if (!url || !el) return undefined;
    let alive = true;
    const start = () =>
      loadPeaks(url)
        .then((p) => alive && setPeaks(p))
        .catch(() => {}); // no peaks = the flat line, a finished state
    if (typeof IntersectionObserver === "undefined") {
      start();
      return () => {
        alive = false;
      };
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          start();
        }
      },
      { rootMargin: "100% 0px" }
    );
    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [url]);

  // Static frame, and redraw when the panel is resized.
  const colorKey = `${color.from}|${color.to}`;
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const paint = () => draw(canvas, { peaks, color, numBars, progress: active ? progressOf() : null });
    paint();
    if (typeof ResizeObserver === "undefined") return undefined;
    // Observe the wrapper, not the canvas, and repaint on the next frame:
    // resizing the backing store inside the observer's own callback is what
    // makes browsers report "ResizeObserver loop completed with undelivered
    // notifications".
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(paint);
    });
    ro.observe(wrapRef.current);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peaks, colorKey, numBars, active]);

  function progressOf() {
    const d = getDuration?.() || 0;
    return d > 0 ? Math.min(1, Math.max(0, (getPosition?.() || 0) / d)) : 0;
  }

  // Animate only the row that is playing.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return undefined;
    let raf = 0;
    const tick = () => {
      draw(canvas, { peaks, color, numBars, progress: progressOf() });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, peaks, colorKey, numBars]);

  return (
    <div ref={wrapRef} className="relative h-full w-full">
      <canvas ref={canvasRef} className="absolute inset-0 block h-full w-full" aria-hidden="true" />
      {!url && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-[11px] uppercase tracking-wider text-[#71717A]">
          No preview
        </span>
      )}
    </div>
  );
}
